<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">
                        Provisionamento de Tablet (Kiosk)
                    </v-toolbar-title>
                    <v-spacer/>
                    <v-icon large>mdi-tablet-dashboard</v-icon>
                </v-toolbar>

                <v-alert
                    v-if="!signatureChecksum"
                    type="warning"
                    dense
                    text
                    class="ma-2"
                >
                    O <strong>checksum da assinatura</strong> ainda não está configurado.
                    O QR será gerado, mas o tablet vai recusar o APK no provisionamento
                    até preencher o checksum do keystore de release (em
                    <code>Configurações avançadas</code>).
                </v-alert>

                <v-row class="ma-1" dense>
                    <!-- Formulário -->
                    <v-col cols="12" md="7">
                        <v-card elevation="2">
                            <v-card-title class="subtitle-1 font-weight-bold">
                                Dados da loja / dispositivo
                            </v-card-title>
                            <v-card-text>
                                <v-text-field
                                    v-model="subdomain"
                                    label="Subdomínio da loja *"
                                    hint="Ex.: diego-modelo — parte do endereço app.zapermenu.com.br/SUBDOMINIO"
                                    persistent-hint
                                    outlined
                                    dense
                                    :rules="[v => !!v || 'Obrigatório']"
                                    @input="onSubdomainInput"
                                />

                                <v-text-field
                                    v-model="deviceCode"
                                    label="Código do dispositivo / mesa (device_code)"
                                    hint="UUID da mesa/tablet no ZaperMenu. Deixe vazio p/ cardápio genérico da loja."
                                    persistent-hint
                                    outlined
                                    dense
                                    class="mt-4"
                                />

                                <v-text-field
                                    v-model="adminPin"
                                    label="PIN de admin do lojista"
                                    hint="6 dígitos — abre o painel admin no tablet"
                                    persistent-hint
                                    outlined
                                    dense
                                    class="mt-4"
                                    maxlength="6"
                                    @input="adminPin = adminPin.replace(/\D/g, '')"
                                >
                                    <template v-slot:append-outer>
                                        <v-btn small text color="primary" @click="adminPin = genPin()">
                                            <v-icon left small>mdi-dice-multiple</v-icon>Gerar
                                        </v-btn>
                                    </template>
                                </v-text-field>

                                <v-select
                                    v-model="screenMode"
                                    :items="screenModes"
                                    label="Modo de tela"
                                    outlined
                                    dense
                                    class="mt-4"
                                />

                                <v-switch
                                    v-model="standbyEnabled"
                                    label="Reduzir brilho quando ocioso"
                                    hint="Economiza bateria quando ninguém está usando a mesa. O toque restaura o brilho."
                                    persistent-hint
                                    class="mt-4"
                                    inset
                                    dense
                                />

                                <v-text-field
                                    v-if="standbyEnabled"
                                    v-model="standbyMinutes"
                                    label="Minutos sem uso"
                                    hint="Entre 1 e 120 minutos"
                                    persistent-hint
                                    type="number"
                                    min="1"
                                    max="120"
                                    outlined
                                    dense
                                    class="mt-4"
                                    @input="standbyMinutes = clampMinutes(standbyMinutes)"
                                />

                                <v-divider class="my-2"/>
                                <div class="subtitle-2 mb-2">Wi-Fi (opcional — pré-conecta no setup)</div>
                                <v-select
                                    v-model="wifiSecurity"
                                    :items="wifiSecurityTypes"
                                    label="Segurança do Wi-Fi"
                                    outlined
                                    dense
                                />
                                <template v-if="wifiSecurity !== 'NONE'">
                                    <v-text-field
                                        v-model="wifiSsid"
                                        label="Nome da rede (SSID)"
                                        outlined
                                        dense
                                    />
                                    <v-text-field
                                        v-model="wifiPassword"
                                        label="Senha do Wi-Fi"
                                        outlined
                                        dense
                                    />
                                </template>

                                <v-expansion-panels flat class="mt-2">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header class="px-0">
                                            <span class="subtitle-2">
                                                <v-icon small left>mdi-cog</v-icon>Configurações avançadas
                                            </span>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <v-text-field
                                                v-model="apkUrl"
                                                label="URL do APK (GitHub Releases)"
                                                outlined
                                                dense
                                            />
                                            <v-text-field
                                                v-model="signatureChecksum"
                                                label="Checksum da assinatura (SHA-256 Base64 URL-safe)"
                                                hint="Sai do keystore de release. Muda só se trocar a chave de assinatura."
                                                persistent-hint
                                                outlined
                                                dense
                                            />
                                            <v-text-field
                                                v-model="component"
                                                label="Component (package/DeviceAdminReceiver)"
                                                outlined
                                                dense
                                                class="mt-4"
                                            />
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                                <v-alert dense text color="blue-grey" class="mt-3 mb-0">
                                    <div class="caption">O tablet vai abrir:</div>
                                    <div class="body-2 font-weight-medium" style="word-break: break-all;">
                                        {{ menuUrl }}
                                    </div>
                                </v-alert>
                            </v-card-text>
                        </v-card>

                        <v-card elevation="2" class="mt-2">
                            <v-card-title class="subtitle-1 font-weight-bold">
                                Passos do lojista (ler pro cliente)
                            </v-card-title>
                            <v-card-text>
                                <ol class="body-2" style="line-height: 1.9;">
                                    <li>Tablet <strong>zerado</strong> (novo ou após factory reset — sem conta Google logada).</li>
                                    <li>Na tela de boas-vindas do Android, <strong>tocar 6× no mesmo ponto</strong> → abre o leitor de QR.</li>
                                    <li>Conectar no Wi-Fi (ou já vem no QR).</li>
                                    <li><strong>Escanear este QR</strong> → o tablet baixa o app e vira Device Owner sozinho.</li>
                                    <li>Pronto: sobe travado no cardápio. Admin = 5 toques/toque longo no canto superior esquerdo + PIN.</li>
                                </ol>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- QR + JSON -->
                    <v-col cols="12" md="5">
                        <v-card elevation="2">
                            <v-card-title class="subtitle-1 font-weight-bold">QR de provisionamento</v-card-title>
                            <v-card-text class="text-center">
                                <div v-if="qrError" class="error--text body-2">{{ qrError }}</div>
                                <img
                                    v-else-if="qrDataUrl"
                                    :src="qrDataUrl"
                                    alt="QR de provisionamento"
                                    style="width: 100%; max-width: 340px; image-rendering: pixelated;"
                                />
                                <div class="d-flex justify-center mt-3" style="gap: 8px;">
                                    <v-btn small color="primary" :disabled="!qrDataUrl" @click="baixarQr">
                                        <v-icon left small>mdi-download</v-icon>Baixar PNG
                                    </v-btn>
                                    <v-btn small outlined :disabled="!qrDataUrl" @click="imprimir">
                                        <v-icon left small>mdi-printer</v-icon>Imprimir
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>

                        <v-card elevation="2" class="mt-2">
                            <v-card-title class="subtitle-1 font-weight-bold">
                                JSON
                                <v-spacer/>
                                <v-btn small text color="primary" @click="copiarJson">
                                    <v-icon left small>mdi-content-copy</v-icon>Copiar
                                </v-btn>
                            </v-card-title>
                            <v-card-text>
                                <pre class="payload-json">{{ payloadJson }}</pre>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import qrcode from 'qrcode-generator';
import BaseContainer from '@/components/BaseContainer';
import {
    PROVISIONING_DEFAULTS,
    SCREEN_MODES,
    STANDBY_DEFAULTS,
    WIFI_SECURITY_TYPES,
    generateAdminPin,
    tabletMenuUrl,
    buildProvisioningPayload,
} from './provisioning';

export default {
    name: 'ProvisionamentoTablet',

    components: { BaseContainer },

    data: () => ({
        subdomain: '',
        deviceCode: '',
        adminPin: generateAdminPin(),
        screenMode: 'ALWAYS_ON',
        standbyEnabled: STANDBY_DEFAULTS.enabled,
        standbyMinutes: STANDBY_DEFAULTS.minutes,
        wifiSecurity: 'NONE',
        wifiSsid: '',
        wifiPassword: '',

        // Configuráveis (padrão vindo do módulo de provisionamento)
        component: PROVISIONING_DEFAULTS.component,
        apkUrl: PROVISIONING_DEFAULTS.apkUrl,
        signatureChecksum: PROVISIONING_DEFAULTS.signatureChecksum,

        screenModes: SCREEN_MODES,
        wifiSecurityTypes: WIFI_SECURITY_TYPES,

        qrDataUrl: '',
        qrError: '',
    }),

    computed: {
        payload() {
            return buildProvisioningPayload({
                subdomain: this.subdomain,
                deviceCode: this.deviceCode,
                adminPin: this.adminPin,
                screenMode: this.screenMode,
                standbyEnabled: this.standbyEnabled,
                standbyMinutes: this.standbyMinutes,
                wifiSsid: this.wifiSsid,
                wifiPassword: this.wifiPassword,
                wifiSecurity: this.wifiSecurity,
                component: this.component,
                apkUrl: this.apkUrl,
                signatureChecksum: this.signatureChecksum,
            });
        },

        payloadJson() {
            return JSON.stringify(this.payload, null, 2);
        },

        payloadCompact() {
            return JSON.stringify(this.payload);
        },

        menuUrl() {
            return tabletMenuUrl(this.subdomain, this.deviceCode);
        },
    },

    watch: {
        payloadCompact: {
            immediate: true,
            handler() {
                this.renderQr();
            },
        },
    },

    methods: {
        genPin: generateAdminPin,

        /** O app aceita 1..120; sanear aqui evita gerar um QR com valor que ele ignora. */
        clampMinutes(valor) {
            const n = parseInt(valor, 10);
            if (isNaN(n)) return '';
            return String(Math.min(120, Math.max(1, n)));
        },

        onSubdomainInput() {
            // subdomínio: só minúsculas, dígitos e hífen (mesma regra do app)
            this.subdomain = (this.subdomain || '')
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, '');
        },

        renderQr() {
            this.qrError = '';
            const data = this.payloadCompact;
            // Tenta EC 'M' (mais robusto); se o payload não couber, cai p/ 'L' (mais capacidade).
            for (const ec of ['M', 'L']) {
                try {
                    const qr = qrcode(0, ec); // 0 = auto-detecta a menor versão
                    qr.addData(data);
                    qr.make();
                    this.qrDataUrl = qr.createDataURL(6, 4);
                    return;
                } catch (e) {
                    // tenta o próximo nível
                }
            }
            this.qrDataUrl = '';
            this.qrError = 'Payload grande demais para um QR. Reduza os dados (ex.: Wi-Fi).';
        },

        copiarJson() {
            navigator.clipboard.writeText(this.payloadJson)
                .then(() => this.notify('JSON copiado'))
                .catch(() => this.notify('Não foi possível copiar', 'warning'));
        },

        baixarQr() {
            if (!this.qrDataUrl) return;
            const a = document.createElement('a');
            a.href = this.qrDataUrl;
            const nome = this.subdomain || 'tablet';
            const mesa = this.deviceCode ? `-${this.deviceCode.slice(0, 8)}` : '';
            a.download = `qr-provisionamento-${nome}${mesa}.png`;
            a.click();
        },

        imprimir() {
            if (!this.qrDataUrl) return;
            const w = window.open('', '_blank');
            if (!w) return;
            w.document.write(`
                <html><head><title>QR Provisionamento — ${this.subdomain || ''}</title></head>
                <body style="text-align:center;font-family:sans-serif;padding:24px;">
                    <h2>Provisionamento do Tablet</h2>
                    <p><strong>Loja:</strong> ${this.subdomain || '—'}${this.deviceCode ? ' · Mesa: ' + this.deviceCode : ''}</p>
                    <p><strong>PIN admin:</strong> ${this.adminPin || '—'}</p>
                    <p><strong>Standby:</strong> ${this.standbyEnabled ? 'brilho reduzido após ' + this.standbyMinutes + ' min sem uso' : 'desligado'}</p>
                    <img src="${this.qrDataUrl}" style="width:360px;image-rendering:pixelated;" />
                    <p style="font-size:12px;color:#666;">Tablet zerado → 6 toques na tela de boas-vindas → escanear.</p>
                    <p style="font-size:12px;color:#666;">Trocar a mesa depois: 5 toques no canto superior esquerdo → PIN → Trocar mesa.</p>
                </body></html>
            `);
            w.document.close();
            w.focus();
            w.print();
        },
    },
};
</script>

<style scoped>
.payload-json {
    background: rgba(127, 127, 127, 0.12);
    border-radius: 6px;
    padding: 10px;
    font-size: 11px;
    line-height: 1.4;
    max-height: 320px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
